/**
 * One watched site: is it up now, how has it been, how fast does it answer,
 * what went wrong and when, is it safe, and what is about to run out.
 *
 * The order is the order of the questions somebody arrives with. They came from
 * an alert or from a red row, so the first thing on the page is the state and,
 * when an outage is open, since when and why. The history follows (90 days, then
 * the response times, which open on the last 24 hours and zoom and scroll back
 * through every check still kept, then the weekly page speed, which is a
 * different number from the server's response time and says so), then the outages one by
 * one, then what the site's web maintenance package gives it
 * (docs/saidiseire-paketid.md), then the weekly security scan and the inside
 * view - the WordPress reporter plugin's (item F) for an outside site, the
 * platform's own (item K) for a site that runs on our platform - then the two expiry dates
 * that are checked once a day and matter once a year, and last the monthly
 * report, which summarises everything above it. The package card sits above
 * the security, reporter and expiry cards because it explains them: a package
 * can leave any of them out. The speed, security and monthly cards
 * (docs/saidiseire-kiirus-turve-raport.md) each read their own route, like the
 * reporter card.
 *
 * Actions sit in the head and answer to `manage:crm.monitoring`; the page itself
 * is `view:crm`, like every CRM register.
 */
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Info, Loader2, Pause, Pencil, Play, RefreshCw, SearchX, Trash2 } from "lucide-react";
import { useI18n } from "../../../i18n/I18nProvider";
import {
  adviceKey,
  formatMs,
  formatUptime,
  neverSeenWorking,
  notChecked,
  planLabel,
  shownUptime,
  stateKey,
  stateTone,
  type MonitorRow,
} from "../../../lib/monitoring";
import { Btn, CardHead, EmptyState, PageHead, StatusPill, fmtDateTime, fmtStamp } from "../../ui";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { ExpiryCards } from "./ExpiryCards";
import { IncidentHistory } from "./IncidentHistory";
import { MonitorDialog } from "./MonitorDialog";
import { PackageCard } from "./PackageCard";
import { PlatformCard } from "./platform";
import { MonthlyCard } from "./monthly";
import { ReporterCard } from "./reporter";
import { ResponseChart } from "./response";
import { SecurityCard } from "./security";
import { SpeedCard } from "./speed";
import { UptimeStrip } from "./UptimeStrip";
import { useMonitor } from "./useMonitor";
import { useMonitorActions } from "./useMonitorActions";
import { useMonitorPlans } from "./useMonitorPlans";
import { codeText, durationText, type T } from "./words";

/**
 * The four figures under the head, then the sentences that explain the state.
 *
 * A confirmed outage says since when, for how long and why. Everything else
 * says what the last check said, and - for a site we cannot judge - what the
 * operator can do about it (`adviceKey`): a site never seen working needs its
 * address looked at, a firewall needs WebAdminBot allow-listed, a password wall
 * needs a pause. A paused site, or one whose client is inactive, says that it
 * is not being checked, because its last state is history.
 */
function StatusCard({ site, t }: { site: MonitorRow; t: T }) {
  const inc = site.openIncident;
  const stopped = notChecked(site);
  const said = !stopped && site.state !== "up" ? codeText(t, site.lastCode) : null;
  const incWhy = inc ? codeText(t, inc.code) : null;
  const advice = stopped ? null : adviceKey(site);
  const lines: string[] = [];
  if (site.paused) lines.push(t("monitoring.page.pausedNote"));
  else if (!site.clientActive) lines.push(t("monitoring.page.inactiveNote"));
  if (inc) {
    lines.push(t("monitoring.page.downSince", { when: fmtDateTime(inc.startedAt), time: durationText(t, inc.durationSeconds) }));
    if (incWhy) lines.push(t("monitoring.page.downWhy", { what: incWhy }));
  }
  // The last check only adds something when it says other than the outage does.
  if (said && said !== incWhy) lines.push(t("monitoring.page.lastSaid", { what: said }));

  return (
    <section className="crm-card mb-4">
      <div className="crm-pstats grid-cols-2 lg:grid-cols-4">
        {/* No pill in here: `.crm-pstat span` is the caption style and would
            flatten the pill's own span. The pill sits beside the title. */}
        <div className="crm-pstat" title={fmtDateTime(site.stateSince)}>
          <b className="crm-tnum">{site.stateSince ? fmtStamp(site.stateSince) : "-"}</b>
          <span>{t("monitoring.page.since")}</span>
        </div>
        <div className="crm-pstat">
          <b className="crm-tnum">{formatUptime(shownUptime(site))}</b>
          <span>{t("monitoring.page.uptime30")}</span>
        </div>
        <div className="crm-pstat">
          <b className="crm-tnum">{formatMs(site.p50Ms7d)}</b>
          <span>{t("monitoring.page.median7")}</span>
        </div>
        <div className="crm-pstat">
          <b className="crm-tnum">{site.lastCheckAt ? fmtStamp(site.lastCheckAt) : t("monitoring.never")}</b>
          <span>
            {site.lastMs !== null && site.state === "up"
              ? t("monitoring.page.lastAnswered", { ms: formatMs(site.lastMs) })
              : t("monitoring.page.lastCheck")}
          </span>
        </div>
      </div>
      {lines.length > 0 ? <p className="crm-sub px-[26px] pb-4 pt-1">{lines.join(" ")}</p> : null}
      {advice ? (
        <p className="crm-sub flex items-start gap-2 px-[26px] pb-4 text-[13px]">
          <Info className="mt-0.5 size-4 shrink-0 text-[var(--color-warning)]" strokeWidth={1.8} />
          <span>{t(`monitoring.advice.${advice}`)}</span>
        </p>
      ) : null}
    </section>
  );
}

export function MonitorPage({ canManage, canEditPlans, t }: { canManage: boolean; canEditPlans: boolean; t: T }) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { locale } = useI18n();
  const { data, failed, notFound, replaceSite, reload } = useMonitor(id);
  const plans = useMonitorPlans();
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const actions = useMonitorActions({ onChanged: replaceSite, onRemoved: () => navigate("/monitoring") });

  const back = (
    <Link to="/monitoring" className="crm-mut mb-3 inline-flex items-center gap-1.5 text-[13px] hover:text-foreground">
      <ArrowLeft className="size-4" />
      {t("monitoring.page.back")}
    </Link>
  );

  if (notFound) {
    return (
      <div className="crm-page">
        {back}
        <div className="crm-card">
          <EmptyState icon={SearchX} title={t("monitoring.page.goneTitle")} body={t("monitoring.page.goneBody")} />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="crm-page">
        {back}
        <div className="crm-card flex justify-center py-16 text-muted-foreground">
          {failed ? (
            <div className="text-center">
              <p className="mb-3 text-[13px] text-[var(--color-destructive)]">{t("monitoring.loadFailed")}</p>
              <Btn small onClick={() => void reload()}>
                {t("monitoring.retry")}
              </Btn>
            </div>
          ) : (
            <Loader2 className="size-5 animate-spin" />
          )}
        </div>
      </div>
    );
  }

  const site = data.site;
  const busy = actions.busyId === site.id;
  // A site with no package (or one that is gone) is told a feature needs one, not that its package leaves it out.
  const hasPackage = data.policy.source === "plan";
  const sub = (
    <>
      <Link to={`/clients/${site.clientId}`} className="hover:underline">
        {site.clientName}
      </Link>
      {" · "}
      <a href={site.url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 hover:underline">
        {site.url}
        <ExternalLink className="size-3" />
      </a>
      {site.planCode ? ` · ${planLabel(plans?.plans ?? null, site.planCode, locale)}` : ""}
      {site.platformSiteId ? ` · ${t("monitoring.platform.badge")}` : ""}
    </>
  );

  return (
    <div className="crm-page">
      {back}
      <PageHead
        title={
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
            {site.label ?? site.host}
            <StatusPill tone={stateTone(site)}>{t(stateKey(site))}</StatusPill>
          </span>
        }
        sub={sub}
        actions={
          canManage ? (
            <>
              {!notChecked(site) ? (
                <Btn small disabled={busy} onClick={() => void actions.checkNow(site)}>
                  {busy ? <Loader2 className="size-[15px] animate-spin" /> : <RefreshCw className="size-[15px]" strokeWidth={1.8} />}
                  {t("monitoring.menu.check")}
                </Btn>
              ) : null}
              <Btn small disabled={busy} onClick={() => void actions.setPaused(site, !site.paused)}>
                {site.paused ? <Play className="size-[15px]" strokeWidth={1.8} /> : <Pause className="size-[15px]" strokeWidth={1.8} />}
                {t(site.paused ? "monitoring.menu.resume" : "monitoring.menu.pause")}
              </Btn>
              <Btn small disabled={busy} onClick={() => setEditing(true)}>
                <Pencil className="size-[15px]" strokeWidth={1.8} />
                {t("monitoring.menu.edit")}
              </Btn>
              <Btn small tone="danger" disabled={busy} onClick={() => setRemoving(true)}>
                <Trash2 className="size-[15px]" strokeWidth={1.8} />
                {t("monitoring.menu.remove")}
              </Btn>
            </>
          ) : null
        }
      />

      <StatusCard site={site} t={t} />

      <section className="crm-card mb-4">
        <CardHead title={t("monitoring.page.stripTitle")} sub={t("monitoring.page.stripSub")} />
        <div className="px-[26px] pb-6">
          {/* A site never seen working, paused or not, has no day to show: the
              rollup observes nothing before its first answer. */}
          <UptimeStrip days={neverSeenWorking(site) ? [] : data.days} t={t} />
        </div>
      </section>

      <section className="crm-card mb-4">
        <CardHead title={t("monitoring.page.chartTitle")} sub={t("monitoring.page.chartSub")} />
        <div className="px-[18px] pb-5">
          <ResponseChart
            monitorId={site.id}
            checkMinutes={data.policy.checkMinutes}
            historyFrom={data.historyFrom}
            liveKey={site.lastCheckAt}
            t={t}
          />
        </div>
      </section>

      <SpeedCard monitorId={site.id} allowed={data.policy.speedTest} hasPackage={hasPackage} canManage={canManage} t={t} />

      <section className="crm-card mb-4">
        <CardHead title={t("monitoring.page.incidentsTitle")} sub={t("monitoring.page.incidentsSub")} />
        <IncidentHistory incidents={data.incidents} t={t} />
      </section>

      <PackageCard
        planCode={site.planCode}
        policy={data.policy}
        plans={plans?.plans ?? null}
        canEditPlans={canEditPlans}
        platform={site.platformSiteId !== null}
        t={t}
      />

      <SecurityCard monitorId={site.id} allowed={data.policy.securityScan} hasPackage={hasPackage} canManage={canManage} t={t} />

      {site.platformSiteId ? (
        <PlatformCard monitorId={site.id} t={t} />
      ) : (
        <ReporterCard monitorId={site.id} canManage={canManage} allowed={data.policy.wpReporter} t={t} />
      )}

      <ExpiryCards
        url={site.url}
        cert={site.cert}
        domain={site.domain}
        certOn={data.policy.certExpiry}
        domainOn={data.policy.domainExpiry}
        t={t}
      />

      <MonthlyCard monitorId={site.id} allowed={data.policy.monthlyReport} hasPackage={hasPackage} canManage={canManage} t={t} />

      {editing ? (
        <MonitorDialog
          site={site}
          t={t}
          onClose={() => setEditing(false)}
          onSaved={replaceSite}
          onOpen={(to) => navigate(`/monitoring/${to}`)}
        />
      ) : null}
      {removing ? (
        <ConfirmModal
          title={t("monitoring.remove.title")}
          body={t("monitoring.remove.body", { host: site.host })}
          confirmLabel={t("monitoring.menu.remove")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            await actions.remove(site);
            setRemoving(false);
          }}
          onClose={() => setRemoving(false)}
        />
      ) : null}
    </div>
  );
}
